import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import readXlsxFile from 'read-excel-file';
import { useDispatch, useSelector } from 'react-redux';
import {
  last,
  keys,
  getOr,
  isEmpty,
  zipObject,
  isEqual,
  reduce,
  values,
  set,
  filter,
  toInteger,
  omit,
  has,
  trim
} from 'lodash/fp';
import {
  Button,
  Content,
  Uploader,
  Panel,
  Grid,
  Row,
  Col,
  Icon,
  Message,
  Alert,
  Input
} from 'rsuite';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import IMPORT_COLUMNS from '../../../constants/inventory/ImportItemQuantityColumns';
import InventoryActions from '../../../constants/inventory/InventoryActions';
import {
  AddActionLabels,
  RemoveActionLabels
} from '../../../constants/inventory/Reasons';
import { modifyAccess } from '../../../helpers/permission';
import AccessControl from '../../../components/AccessControl';
import AccessDenied from '../../../components/AccessDenied';
import InventoryPageLayout from '../../../containers/inventory/PageLayout';
import PageHeader from '../../../components/PageHeader';
import { importInventoryItemQuantity } from '../../../redux/Inventory/sagas/item/routines';
import ImportItemsQuantityResult from '../../../components/inventory/ImportItemsQuantityResult ';
import { getItemsQuantity } from '../../../redux/Inventory/selectors/components/import-items-quantity-result';
import { getItemLocationZoneOptions } from '../../../redux/Inventory/selectors/options/item-location-zone';
import { fetchInventoryItemLocationZoneOptions } from '../../../redux/sagas/routines';
import {
  clearImportItemsQty,
  failedImportItemQty
} from '../../../redux/Inventory/actions/components/import-items-quantity-result';

const StepHeading = styled.p`
  font-size: 14px;
  margin-bottom: 10px;
`;

const StepWrapper = styled.div`
  margin-bottom: 10px;
`;

const ImportMessageLabel = styled.div`
  font-size: 16px;
`;

const SummaryItem = ({ count, quantity, actionLabel, ...props }) => (
  <div {...props}>
    <small>
      {actionLabel} - <strong>{count}</strong> rows with
      <strong> {quantity} </strong>
      total items quantity
    </small>
  </div>
);

SummaryItem.propTypes = {
  count: PropTypes.number.isRequired,
  quantity: PropTypes.number.isRequired,
  actionLabel: PropTypes.string.isRequired
};

const DefaultImportNote = 'Updates via import';
const InvalidFormatError = 'Invalid inventory items import format.';
const InvalidQtyError = 'Field quantity is invalid. Must be > 0.';
const NoRowsError =
  'No valid rows found. Quantity and Reason must be provided.';

const ImportAddActionLabels = omit(
  [
    InventoryActions.DONATION_RECEIVED,
    InventoryActions.POS_DEDUCT,
    InventoryActions.POS_CANCELED
  ],
  AddActionLabels
);

const ImportRemoveActionLabels = omit(
  [
    InventoryActions.DONATED,
    InventoryActions.DONATION_REMOVED,
    InventoryActions.POS_ADD,
    InventoryActions.POS_CANCELED
  ],
  RemoveActionLabels
);

const addActionsMapByName = zipObject(
  values(ImportAddActionLabels),
  keys(ImportAddActionLabels)
);
const removeActionsMapByName = zipObject(
  values(ImportRemoveActionLabels),
  keys(ImportRemoveActionLabels)
);

const parseActionValues = actionLabel => {
  const actionPrefix = (actionLabel || '').substring(0, 1);
  const actionName = (actionLabel || '').substring(2);
  if (actionPrefix === '+') {
    const action = getOr('', actionName, addActionsMapByName);
    return { actionName, action, mainAction: 'ADD' };
  }
  if (actionPrefix === '-') {
    const action = getOr('', actionName, removeActionsMapByName);
    return { actionName, action, mainAction: 'DEDUCT' };
  }
  return { actionName, action: '', mainAction: '' };
};

export default () => {
  const dispatch = useDispatch();
  const importItemsResult = useSelector(getItemsQuantity);
  const zoneOptions = useSelector(getItemLocationZoneOptions);
  const [fileList, setFileList] = useState([]);
  const [itemsToImport, setItemsToImport] = useState([]);
  const [importStarted, setImportStarted] = useState(false);
  const [note, setNote] = useState(DefaultImportNote);

  const hasItems = !isEmpty(itemsToImport);
  const importFinished =
    importStarted && itemsToImport.length === importItemsResult.length;
  const importInProgress = importStarted && !importFinished;
  const importSummary = useMemo(
    () => {
      if (!importFinished) return {};

      return reduce(
        (result, { mainAction, status, quantity: qty }) => {
          if (status === 1) {
            const count = getOr(0, `${mainAction}.count`, result) + 1;
            const quantity = getOr(0, `${mainAction}.quantity`, result) + qty;
            return set(mainAction, { count, quantity }, result);
          }
          const count = getOr(0, 'FAILED.count', result) + 1;
          const quantity = getOr(0, 'FAILED.quantity', result) + qty;
          return set('FAILED', { count, quantity }, result);
        },
        {},
        importItemsResult
      );
    },
    [importFinished]
  );

  useEffect(() => {
    dispatch(fetchInventoryItemLocationZoneOptions());
  }, []);

  const handleChangeNote = value => {
    setNote(value);
  };

  const handleStartImport = async () => {
    setImportStarted(true);
    dispatch(clearImportItemsQty());

    const zonesMapByName = reduce(
      (result, { label, value, location }) =>
        set(`${trim(location)} / ${trim(label)}`, value, result),
      {},
      zoneOptions
    );

    itemsToImport.forEach((each, i) => {
      const counter = i + 1;
      // eslint-disable-next-line no-unused-vars
      const [item, zoneName, _, qty, actionLabel] = each;
      const quantity = toInteger(qty) || 0;
      const [itemName, itemId] = item.split(' -> ', 2);
      const zoneId = getOr(null, trim(zoneName), zonesMapByName);
      const { actionName, action, mainAction } = parseActionValues(actionLabel);
      const actionParams = {
        counter,
        itemId,
        itemName,
        zoneName,
        actionName,
        mainAction
      };
      if (quantity > 0) {
        const body = { action, quantity, zoneId, note };
        dispatch(importInventoryItemQuantity({ ...actionParams, body }));
      } else {
        const error = { error: [{ message: InvalidQtyError }] };
        dispatch(failedImportItemQty({ ...actionParams, quantity, error }));
      }
    });
  };

  const handleSelectFile = async files => {
    if (isEmpty(files)) return;

    setItemsToImport([]);
    const file = last(files);
    const [dataColumns, ...rows] = await readXlsxFile(file.blobFile);
    const ReasonColumn = 4;
    const validRows = filter(ReasonColumn, rows);

    if (!isEqual(values(IMPORT_COLUMNS), dataColumns)) {
      Alert.error(InvalidFormatError, 10000);
    } else if (isEmpty(validRows)) {
      Alert.error(NoRowsError, 10000);
    } else {
      setItemsToImport(filter(ReasonColumn, rows));
    }
    setFileList([file]);
  };

  const handleRemoveFile = () => {
    setFileList([]);
    setItemsToImport([]);
  };

  const handleReset = () => {
    setFileList([]);
    setItemsToImport([]);
    setImportStarted(false);
    setNote(DefaultImportNote);
    dispatch(clearImportItemsQty());
  };

  return (
    <InventoryPageLayout>
      <AccessControl
        allowWhenAll={[modifyAccess(PERMISSIONS.ITEM)]}
        fallback={<AccessDenied />}
      >
        <PageHeader title="Import Items Quantity" />
        <Content>
          <Panel bordered>
            <Grid fluid>
              <Row>
                <Col xs={24} md={6}>
                  <StepWrapper>
                    <StepHeading>
                      <strong>STEP 1:</strong> &nbsp;Set import note
                    </StepHeading>
                    <Input
                      value={note}
                      onChange={handleChangeNote}
                      style={{ width: 360 }}
                    />
                  </StepWrapper>
                </Col>
                <Col xs={24} md={6}>
                  <StepWrapper>
                    <StepHeading>
                      <strong>STEP 2:</strong> &nbsp;Select inventory items
                      quantity file to upload
                    </StepHeading>
                    <Uploader
                      multiple={false}
                      accept=".xls, .xlsx"
                      fileList={fileList}
                      onChange={handleSelectFile}
                      onRemove={handleRemoveFile}
                      style={{ maxWidth: 400 }}
                      toggleComponentClass={props => (
                        <Button {...props}>
                          <Icon icon="upload" />
                          &nbsp; Upload inventory items
                        </Button>
                      )}
                      disabled={importInProgress || !note}
                    />
                  </StepWrapper>
                </Col>
                <Col xs={24} md={6}>
                  <StepWrapper>
                    <StepHeading>
                      <strong>STEP 3:</strong> &nbsp;Click the button to start
                      importing items quantity
                    </StepHeading>
                    <Button
                      appearance="primary"
                      disabled={!hasItems || importStarted}
                      onClick={handleStartImport}
                    >
                      <Icon icon="import" />
                      &nbsp; Import {itemsToImport.length || '#'} items&nbsp;
                    </Button>
                  </StepWrapper>
                </Col>
                <Col xs={24} md={4}>
                  {hasItems && (
                    <StepWrapper>
                      <StepHeading>
                        Reset settings to default
                        {!isEmpty(importItemsResult) && ' and item list'}
                      </StepHeading>

                      <Button onClick={handleReset} disabled={importInProgress}>
                        <Icon icon="refresh" />
                        &nbsp; Reset
                      </Button>
                    </StepWrapper>
                  )}
                </Col>
              </Row>
            </Grid>
          </Panel>

          <br />

          {importInProgress && (
            <Message
              showIcon
              type="info"
              title="Importing Items..."
              description={
                <ImportMessageLabel>
                  <strong>
                    {itemsToImport.length - importItemsResult.length}
                  </strong>
                  &nbsp;items left... &nbsp;
                </ImportMessageLabel>
              }
            />
          )}
          {importFinished && (
            <Message
              showIcon
              type="success"
              title="Import Finished!"
              description={
                <ImportMessageLabel>
                  <strong>{importItemsResult.length}</strong> total rows has
                  been processed.
                  {has('ADD', importSummary) && (
                    <SummaryItem
                      actionLabel="ADDED"
                      count={importSummary.ADD.count}
                      quantity={importSummary.ADD.quantity}
                    />
                  )}
                  {has('DEDUCT', importSummary) && (
                    <SummaryItem
                      actionLabel="DEDUCTED"
                      count={importSummary.DEDUCT.count}
                      quantity={importSummary.DEDUCT.quantity}
                    />
                  )}
                  {has('FAILED', importSummary) && (
                    <SummaryItem
                      actionLabel="FAILED"
                      count={importSummary.FAILED.count}
                      quantity={importSummary.FAILED.quantity}
                    />
                  )}
                </ImportMessageLabel>
              }
            />
          )}
          <br />
          {importStarted && (
            <ImportItemsQuantityResult items={importItemsResult} />
          )}
        </Content>
      </AccessControl>
    </InventoryPageLayout>
  );
};
